import type { ComponentType } from 'react';
import { ClipboardList, User, Wrench } from 'lucide-react';
import type { IssueDto, ProjectAutonomy } from '@aiai/shared';
import { assigneeFor } from '@aiai/shared';

const ICON: Record<string, ComponentType<{ className?: string }>> = {
  planner: ClipboardList, // matches the Team tab
  builder: Wrench,
  you: User,
};

const NAME: Record<string, string> = {
  planner: 'Planner',
  builder: 'Builder',
  you: 'You',
  none: '—',
};

/**
 * The "who has the ball" avatar, derived (via `assigneeFor`) from the issue's
 * state + latest run. Agents (Planner / Builder) while they work; You at the
 * exception points, where a soft orange ring makes "your turn" read without a
 * separate alarm. `showReason` adds the short reason next to the avatar (issue
 * page); omitted on dense board rows.
 */
export function AssigneeChip({
  issue,
  showReason = false,
  autonomy,
}: {
  issue: Pick<IssueDto, 'state' | 'latestRun' | 'tags'>;
  showReason?: boolean;
  /** Project autonomy dial. Drives the assignee for `open` issues (Builder
   *  "queued" vs You "run") and the `proposed` split (approve/plan/queued). */
  autonomy?: ProjectAutonomy;
}) {
  const a = assigneeFor(issue, { autonomy });
  if (a.actor === 'none') return null;
  const name = NAME[a.actor];
  const Icon = ICON[a.actor];

  return (
    <span
      className="inline-flex items-center gap-1.5"
      title={`${name} · ${a.reason}`}
      aria-label={`Assigned to ${name} — ${a.reason}`}
    >
      <span
        className={[
          'grid h-5 w-5 flex-none place-items-center rounded-full',
          a.needsYou ? 'bg-signal/15 text-signal ring-2 ring-signal/40' : 'bg-surface-2 text-ink-muted',
        ].join(' ')}
        aria-hidden="true"
      >
        <Icon className="h-3 w-3" />
      </span>
      {showReason && (
        <span className={a.needsYou ? 'text-xs font-semibold text-signal' : 'text-xs text-ink-muted'}>
          {name} <span className="text-ink-faint">· {a.reason}</span>
        </span>
      )}
    </span>
  );
}
